import React from 'react';
import { ShieldIcon, ArrowLeftIcon } from './icons';
import { usePlanStore } from '../store/usePlanStore';

interface Props {
    publicMode?: boolean;
}

export default function PrivacyPage({ publicMode }: Props = {}) {
    const goToDashboard = usePlanStore((s) => s.goToDashboard);

    const handleBack = () => {
        if (publicMode) {
            window.location.href = '/';
        } else {
            goToDashboard();
        }
    };

    return (
        <div className="max-w-3xl mx-auto animate-fade-in">
            <button
                onClick={handleBack}
                className="mb-4 flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700 font-medium"
            >
                <ArrowLeftIcon size={14} /> Powrót
            </button>

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-950 flex items-center justify-center">
                        <ShieldIcon size={22} className="text-purple-600" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Polityka prywatności</h1>
                        <p className="text-xs text-slate-400">Ostatnia aktualizacja: {new Date().toLocaleDateString('pl-PL')}</p>
                    </div>
                </div>

                <div className="prose dark:prose-invert prose-sm max-w-none space-y-4 text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                    <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mt-6">1. Administrator danych</h2>
                    <p>
                        Administratorem danych osobowych jest autor projektu studenckiego APEXBuilder. Serwis nie jest prowadzony
                        w ramach działalności gospodarczej. W razie pytań dotyczących przetwarzania danych, kontakt jest możliwy
                        poprzez formularz w Serwisie.
                    </p>

                    <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mt-6">2. Jakie dane zbieramy</h2>
                    <p>W ramach korzystania z Serwisu przetwarzamy następujące dane:</p>
                    <ul className="list-disc list-inside space-y-1">
                        <li><strong>Dane rejestracyjne:</strong> imię, adres e-mail, hasło (przechowywane w formie zaszyfrowanej).</li>
                        <li><strong>Dane treningowe:</strong> plany treningowe, ćwiczenia, parametry treningowe - tworzone przez Użytkownika.</li>
                        <li><strong>Dane z ankiet:</strong> odpowiedzi na ankiety (imię, e-mail respondenta, odpowiedzi na pytania) - przetwarzane w celu analizy treningowej.</li>
                        <li><strong>Dane techniczne:</strong> adres IP, agent przeglądarki - zbierane automatycznie w celu zabezpieczenia Serwisu.</li>
                        <li><strong>Pliki cookies:</strong> sesyjne pliki cookies niezbędne do działania uwierzytelniania.</li>
                    </ul>

                    <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mt-6">3. Cel przetwarzania</h2>
                    <p>Dane przetwarzamy w następujących celach:</p>
                    <ul className="list-disc list-inside space-y-1">
                        <li>Świadczenie usług Serwisu (tworzenie planów, ankiet, analiz).</li>
                        <li>Uwierzytelnianie i bezpieczeństwo konta.</li>
                        <li>Komunikacja z Użytkownikiem (powiadomienia o nowych odpowiedziach).</li>
                        <li>Zapewnienie bezpieczeństwa (ochrona przed nadużyciami).</li>
                    </ul>

                    <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mt-6">4. Podstawa prawna</h2>
                    <p>Dane przetwarzamy na podstawie:</p>
                    <ul className="list-disc list-inside space-y-1">
                        <li>Art. 6 ust. 1 lit. a RODO - zgoda Użytkownika (rejestracja).</li>
                        <li>Art. 6 ust. 1 lit. b RODO - wykonanie umowy (świadczenie usług).</li>
                        <li>Art. 6 ust. 1 lit. f RODO - prawnie uzasadniony interes (bezpieczeństwo).</li>
                    </ul>

                    <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mt-6">5. Udostępnianie danych</h2>
                    <p>Dane mogą być udostępniane następującym podmiotom:</p>
                    <ul className="list-disc list-inside space-y-1">
                        <li><strong>MongoDB Atlas</strong> - hosting bazy danych (USA/EU).</li>
                        <li><strong>Render.com</strong> - hosting serwera (USA).</li>
                        <li><strong>Vercel</strong> - hosting frontendu (USA).</li>
                        <li><strong>Resend</strong> - wysyłka e-maili weryfikacyjnych.</li>
                    </ul>
                    <p>Dane nie są sprzedawane ani udostępniane w celach marketingowych.</p>

                    <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mt-6">6. Prawa użytkownika (RODO)</h2>
                    <p>Zgodnie z RODO przysługują Ci następujące prawa:</p>
                    <ul className="list-disc list-inside space-y-1">
                        <li><strong>Prawo dostępu</strong> - możesz sprawdzić, jakie dane przetwarzamy.</li>
                        <li><strong>Prawo do sprostowania</strong> - możesz poprawić swoje dane.</li>
                        <li><strong>Prawo do usunięcia</strong> - możesz zażądać usunięcia konta i danych.</li>
                        <li><strong>Prawo do przenoszenia</strong> - możesz eksportować swoje plany (JSON/PDF).</li>
                        <li><strong>Prawo do cofnięcia zgody</strong> - możesz wycofać zgodę w dowolnym momencie.</li>
                        <li><strong>Prawo do skargi</strong> - możesz złożyć skargę do PUODO (Prezes Urzędu Ochrony Danych Osobowych).</li>
                    </ul>

                    <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mt-6">7. Pliki cookies</h2>
                    <p>Serwis wykorzystuje wyłącznie niezbędne pliki cookies sesyjne służące do:</p>
                    <ul className="list-disc list-inside space-y-1">
                        <li>Utrzymania sesji logowania.</li>
                        <li>Zapamiętania preferencji motywu (jasny/ciemny).</li>
                    </ul>
                    <p>Nie stosujemy cookies analitycznych ani reklamowych.</p>

                    <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mt-6">8. Okres przechowywania</h2>
                    <p>
                        Dane przechowujemy przez czas korzystania z Serwisu. Po usunięciu konta dane są usuwane w ciągu 30 dni.
                        Jako projekt studencki, Serwis może zostać zamknięty - w takim przypadku wszystkie dane zostaną usunięte.
                    </p>

                    <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mt-6">9. Bezpieczeństwo</h2>
                    <p>Stosujemy następujące środki bezpieczeństwa:</p>
                    <ul className="list-disc list-inside space-y-1">
                        <li>Szyfrowanie haseł (bcrypt).</li>
                        <li>HTTPS na wszystkich połączeniach.</li>
                        <li>Ograniczanie liczby zapytań (rate limiting).</li>
                        <li>Walidacja danych wejściowych.</li>
                        <li>Nagłówki bezpieczeństwa (helmet).</li>
                    </ul>

                    <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mt-6">10. Zmiany polityki</h2>
                    <p>
                        Polityka prywatności może być aktualizowana. O istotnych zmianach informujemy poprzez powiadomienie w Serwisie.
                        Kontynuacja korzystania z Serwisu po zmianach oznacza ich akceptację.
                    </p>
                </div>
            </div>
        </div>
    );
}
